/**
 * useFirestoreCollection - Generic Firestore Collection Hook
 *
 * Provides real-time listener for any Firestore collection with:
 * - Automatic subscription management
 * - Configurable queries (orderBy, where, limit, etc.)
 * - Document parsing with type safety
 * - Loading/error state management
 * - Cache vs server read tracking
 */

import { useEffect } from 'react';
import {
  collection,
  query,
  onSnapshot,
  QueryConstraint,
  QuerySnapshot,
  DocumentData,
  Firestore,
} from 'firebase/firestore';
import { logger } from '../../utils/logger';

export interface UseFirestoreCollectionOptions<T> {
  /** Firestore instance */
  db: Firestore | null;

  /** Collection path (e.g., "artifacts/app/prod/vendors/11/items") */
  collectionPath: string | null;

  /** Query constraints (orderBy, where, limit, etc.) */
  queryConstraints?: QueryConstraint[];

  /**
   * Optional key to force listener re-subscription when query constraints change.
   * Since queryConstraints array references change on every render but the actual
   * query might be the same, this key allows explicit control over when to re-subscribe.
   * Example: `${startDate}_${endDate}` for date-range queries.
   */
  queryKey?: string;

  /** Parse Firestore document into typed object */
  parseDocument: (id: string, data: DocumentData) => T | null;

  /** Callback when data is loaded */
  onDataLoaded?: (data: T[]) => void;

  /** Callback when loading state changes */
  onLoadingChange?: (loading: boolean) => void;

  /** Callback when error occurs */
  onError?: (error: string) => void;

  /** Include metadata changes (for cache tracking) */
  includeMetadataChanges?: boolean;

  /** Custom sort function (applied after Firestore query) */
  sortFn?: (a: T, b: T) => number;

  /** Enable/disable the listener (useful for conditional loading) */
  enabled?: boolean;

  /**
   * Refresh key for forcing listener re-subscription.
   * When this value changes, the listener unsubscribes and resubscribes.
   * Use with useFirebaseStore().visibilityRefreshKey for tab wake reconnection.
   */
  refreshKey?: number;
}

/**
 * Generic hook for listening to Firestore collections
 *
 * @example
 * ```typescript
 * useFirestoreCollection({
 *   db,
 *   collectionPath: getVendorsCollectionPath(locationId),
 *   queryConstraints: [orderBy('sortOrder', 'asc')],
 *   parseDocument: (id, data) => ({
 *     id,
 *     name: data.name || '',
 *     sortOrder: data.sortOrder || 0,
 *   }),
 *   onDataLoaded: setVendors,
 *   onLoadingChange: setLoading,
 *   onError: setError,
 * });
 * ```
 */
export function useFirestoreCollection<T>({
  db,
  collectionPath,
  queryConstraints = [],
  queryKey,
  parseDocument,
  onDataLoaded,
  onLoadingChange,
  onError,
  includeMetadataChanges = true,
  sortFn,
  enabled = true,
  refreshKey,
}: UseFirestoreCollectionOptions<T>) {
  useEffect(() => {
    // Guard: explicitly disabled - don't call onDataLoaded to avoid false empty-data state
    // The consumer will get data when enabled becomes true
    if (!enabled) {
      onLoadingChange?.(false);
      return undefined;
    }

    // Guard: missing dependencies - also don't trigger onDataLoaded
    // This prevents issues before Firebase auth completes
    if (!db || !collectionPath) {
      onLoadingChange?.(false);
      return undefined;
    }

    onLoadingChange?.(true);

    try {
      // Build Firestore query
      const collectionRef = collection(db, collectionPath);
      const q = queryConstraints.length > 0
        ? query(collectionRef, ...queryConstraints)
        : collectionRef;

      logger.firestore('Subscribe', collectionPath);

      // Set up real-time listener
      const unsubscribe = onSnapshot(
        q,
        { includeMetadataChanges },
        (snapshot: QuerySnapshot<DocumentData>) => {
          try {
            const source = snapshot.metadata.fromCache ? 'cache' : 'server';
            const changes = snapshot.docChanges().length;
            logger.firestore('Snapshot', `${collectionPath} (${snapshot.size} docs, ${changes} changes, from ${source})`);

            const items: T[] = [];

            snapshot.forEach((doc) => {
              try {
                const parsed = parseDocument(doc.id, doc.data());
                if (parsed !== null) {
                  items.push(parsed);
                }
              } catch (parseError) {
                logger.error(`[useFirestoreCollection] Error parsing document ${doc.id}:`, parseError);
              }
            });

            // Apply custom sort if provided
            if (sortFn) {
              items.sort(sortFn);
            }

            onDataLoaded?.(items);
            onLoadingChange?.(false);
          } catch (error) {
            logger.error('[useFirestoreCollection] Error processing snapshot:', error);
            onError?.('Failed to process data');
            onLoadingChange?.(false);
          }
        },
        (error) => {
          logger.error('[useFirestoreCollection] Listener error:', error);
          onError?.(error.message || 'Failed to load data');
          onLoadingChange?.(false);
        }
      );

      return () => {
        logger.firestore('Unsubscribe', collectionPath);
        unsubscribe();
      };
    } catch (error) {
      logger.error('[useFirestoreCollection] Setup error:', error);
      onError?.('Failed to initialize listener');
      onLoadingChange?.(false);
      return undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    db,
    collectionPath,
    queryKey,
    enabled,
    includeMetadataChanges,
    refreshKey, // Forces re-subscription when visibility refresh key changes (tab wake)
    // Note: Callbacks (parseDocument, onDataLoaded, onLoadingChange, onError, sortFn) are
    // intentionally omitted to prevent effect re-runs when callback references change.
    // They should be stable references from stores or wrapped in useCallback by the caller.
    // Note: queryConstraints is intentionally omitted - use queryKey for dynamic queries.
  ]);
}
